/**
 * The sheet the shipped `Give an award` menu row opens: the member's first
 * name in the title, the pool name in the subtitle, one selectable row per
 * giveable type, a `Take back` control on every held type, one footer
 * primary, and the per-kind empty line.
 *
 * Rows are hand-rolled rather than the kit's `Radio`, which renders a whole
 * group from an `items` array and has no slot for a trailing per-row
 * action, so it cannot express a held row sitting between two giveable ones.
 */
import {
  useAwardCustomGive,
  useAwardCustomList,
  useAwardCustomTakeBack,
  useAwardGetForMember,
} from "@sportsmet/contest-sdk/private";
import { Button, Dialog } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { awardColorRole, awardSwatchClass } from "../awards/awardChips";
import {
  firstNameOf,
  GIVE_AWARD_SUBMIT_LABEL,
  GIVE_AWARD_TAKE_BACK_LABEL,
  giveAwardKindEmptyLine,
  giveAwardKindLabel,
  giveAwardRowLine,
  giveAwardSubtitle,
  giveAwardTitle,
} from "./giveAwardCopy";
import { buildGiveAwardModel, type typeGiveAwardRow } from "./giveAwardModel";
import type { typeAwardTypeRow } from "./poolAwardsModel";

export const GIVE_AWARD_SHEET_NAME = "give-award";

export type typeGiveAwardSheetProps = {
  open: boolean;
  poolId: string;
  poolName: string;
  targetUserId: string;
  memberDisplayName: string;
  onOpenChange: (open: boolean) => void;
};

export const GiveAwardSheet = ({
  open,
  poolId,
  poolName,
  targetUserId,
  memberDisplayName,
  onOpenChange,
}: typeGiveAwardSheetProps): ReactNode => {
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setSelectedTypeId(null);
  }, [open]);

  const list = useAwardCustomList({ poolId }, { enabled: open });
  const memberAwards = useAwardGetForMember(
    { poolId, targetUserId },
    { enabled: open },
  );
  const give = useAwardCustomGive();
  const takeBack = useAwardCustomTakeBack();

  const medals = (list.data?.data?.medals ?? []) as readonly typeAwardTypeRow[];
  const trophies = (list.data?.data?.trophies ??
    []) as readonly typeAwardTypeRow[];
  const history = memberAwards.data?.data?.customAwardHistory ?? [];
  const model = buildGiveAwardModel({ medals, trophies, history });

  const firstName = firstNameOf(memberDisplayName);

  async function handleGive(): Promise<void> {
    if (selectedTypeId === null) return;
    const result = await give.mutateAsync({
      poolId,
      awardTypeId: selectedTypeId,
      targetUserId,
    });
    if (showApiError(result)) return;
    onOpenChange(false);
  }

  async function handleTakeBack(row: typeGiveAwardRow): Promise<void> {
    if (row.takeBackGrantId === null) return;
    const result = await takeBack.mutateAsync({
      poolId,
      awardGrantId: row.takeBackGrantId,
    });
    showApiError(result);
  }

  function renderRow(row: typeGiveAwardRow): ReactNode {
    const line = giveAwardRowLine({
      kind: row.kind,
      firstName,
      heldCount: row.heldCount,
    });
    const disc = (
      <span
        aria-hidden="true"
        className={`h-8 w-8 shrink-0 rounded-full ${awardSwatchClass(awardColorRole(row.color))}`}
      />
    );

    if (row.heldCount > 0) {
      return (
        <li key={row.awardTypeId} className="flex items-center gap-3 py-3">
          {disc}
          <span className="min-w-0 flex-1">
            <span className="block text-body-strong text-ink">{row.name}</span>
            <span className="block text-caption text-ink-secondary">
              {line}
            </span>
          </span>
          <Button
            variant="outline"
            size="sm"
            data-testid={`give-award-take-back-${row.awardTypeId}`}
            disabled={takeBack.isPending}
            onClick={() => void handleTakeBack(row)}
          >
            {GIVE_AWARD_TAKE_BACK_LABEL}
          </Button>
        </li>
      );
    }

    return (
      <li key={row.awardTypeId} className="flex items-center gap-3 py-3">
        {disc}
        <label className="flex min-w-0 flex-1 items-center gap-3">
          <input
            type="radio"
            name="give-award-type"
            value={row.awardTypeId}
            checked={selectedTypeId === row.awardTypeId}
            onChange={() => {
              setSelectedTypeId(row.awardTypeId);
            }}
          />
          <span className="min-w-0 flex-1">
            <span className="block text-body-strong text-ink">{row.name}</span>
            <span className="block text-caption text-ink-secondary">
              {line}
            </span>
          </span>
        </label>
      </li>
    );
  }

  function renderKindGroup(
    kind: "medal" | "trophy",
    rows: readonly typeGiveAwardRow[],
  ): ReactNode {
    return (
      <section key={kind} className="flex flex-col gap-1">
        {model.showKindLabels ? (
          <h4 className="text-h4 text-ink">{giveAwardKindLabel(kind)}</h4>
        ) : null}
        {rows.length === 0 ? (
          <p
            className="text-body text-ink-secondary"
            data-testid={`give-award-empty-${kind}`}
          >
            {giveAwardKindEmptyLine(kind)}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {rows.map((row) => renderRow(row))}
          </ul>
        )}
      </section>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false);
      }}
      title={giveAwardTitle(firstName)}
      description={giveAwardSubtitle(poolName)}
      className="sm:max-w-[480px]"
      footer={
        <Button
          variant="primary"
          fullWidth
          data-testid="give-award-submit"
          disabled={selectedTypeId === null || give.isPending}
          loading={give.isPending}
          onClick={() => void handleGive()}
        >
          {GIVE_AWARD_SUBMIT_LABEL}
        </Button>
      }
    >
      {renderKindGroup("medal", model.medalRows)}
      {renderKindGroup("trophy", model.trophyRows)}
    </Dialog>
  );
};
